import React, { useCallback, useEffect, useRef, useState } from 'react'
import styles from './CreateCampComponent.module.scss'
import { errorMessages, activeText } from '../constants/Constants'
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  FormControlLabel,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Grid,
  Dialog,
  Radio,
  RadioGroup,
  Tooltip,
} from '@mui/material'
import ScaleLoader from 'react-spinners/ScaleLoader'
import {
  getCampTypes,
  createCamp,
  updateCamp,
  getSpecialization,
  getDoctors,
  getLabPackages,
} from '../../../services/CampManagementService'
import { useAppSelector } from '../../../app/hooks'
import { selectLocations } from '../../location/locationSlice'
import { toast } from 'react-toastify'
import { toastOptions } from '../../lms/models'
import ToastMessage from '../../lms/components/ToastMessage'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import AddIcon from '@mui/icons-material/Add'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { styled } from '@mui/material/styles'
import ToggleButton from '@mui/material/ToggleButton'
import DeleteIcon from '@mui/icons-material/Delete'

const StyledToggleButton = styled(ToggleButton)(() => ({
  '&.MuiToggleButton-root': {
    padding: '10px 5px',
    border: '1px solid transparent',
    minWidth: '50px',
    width: '50px',
    height: '20px',
    borderRadius: '10px',
    position: 'relative',
    color: 'transparent',
    fontSize: '0',
    fontWeight: 500,
    textTransform: 'none',
    backgroundColor: '#f44336', // Default to red (inactive)
    '& .MuiTouchRipple-root': {
      display: 'none', // Disable ripple effect
    },
    '&.Mui-selected': {
      backgroundColor: '#4caf50', // Green when active
      '&::after': {
        content: '""',
        position: 'absolute',
        right: '2px',
        top: '2px',
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        backgroundColor: 'white',
        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
      },
    },
    '&:not(.Mui-selected)': {
      backgroundColor: '#f44336', // Red when inactive
      '&::after': {
        content: '""',
        position: 'absolute',
        left: '2px',
        top: '2px',
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        backgroundColor: 'white',
        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
      },
      '&.Mui-disabled': {
        opacity: 0.5,
      },
    },
  },
}))

type CampType = {
  campTypeId: string
  name: string
  description: string
}

type SpecializationType = {
  code: string
  display: string
}

interface CreateCampComponentProps {
  open: boolean
  handleClose: () => void
  className?: string
  initialData?: any | null
  isEdit?: boolean
  onSaved?: (savedCamp?: any) => void
}

const CreateCamp: React.FC<CreateCampComponentProps> = ({
  open,
  handleClose,
  className = '',
  initialData = null,
  isEdit = false,
  onSaved,
}) => {
  const initializedRef = useRef(false)
  const locations = useAppSelector(selectLocations)
  const activeLocations = locations?.filter(
    (l: any) => l?.resource?.status === activeText
  )

  const [locationId, setLocationId] = useState<string>('')
  const [campName, setCampName] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [campType, setCampType] = useState<CampType | undefined>(undefined)
  const [campFeeType, setCampFeeType] = useState<'paid' | 'free'>('free')
  const [options, setOptions] = useState<CampType[]>([])
  // Lab Package type definition
  interface LabPackage {
    id: string
    name: string
    price: number
    panels: any[]
    lab_tests: any[]
    status: string
    active_from: string | null
    active_till: string | null
    package_id: number | null
    created_by: string
    created_at: string
    updated_by: string
    updated_at: string
  }

  const [labPackages, setLabPackages] = useState<LabPackage[]>([])
  const [isLoadingPackages, setIsLoadingPackages] = useState<boolean>(false)
  const [selectedMalePackage, setSelectedMalePackage] =
    useState<LabPackage | null>(null)
  const [selectedFemalePackage, setSelectedFemalePackage] =
    useState<LabPackage | null>(null)
  const [location, setLocation] = useState<string>('')
  const [date, setDate] = useState<string>('') // yyyy-mm-dd
  const [toDate, setToDate] = useState<string>('') // yyyy-mm-dd
  const [loading, setLoading] = useState<boolean>(false)
  const [specializationOptions, setSpecializationOptions] = useState<
    SpecializationType[]
  >([])
  const [campSponsor, setCampSponsor] = useState<string>('')
  const [expectedFootFall, setExpectedFootFall] = useState<string>('')

  // ECG / PFT notification settings
  const [ecgNormalSend, setEcgNormalSend] = useState<boolean>(false)
  const [ecgAbnormalSend, setEcgAbnormalSend] = useState<boolean>(false)
  const [pftNormalSend, setPftNormalSend] = useState<boolean>(false)
  const [pftAbnormalSend, setPftAbnormalSend] = useState<boolean>(false)
  // const [postalCode, setPostalCode] = useState<string>('')

  const handleGetSpecialization = useCallback(async () => {
    try {
      const response = await getSpecialization()
      if (response?.success) {
        const specializations = Array?.isArray(response?.data)
          ? response?.data
          : []
        setSpecializationOptions(specializations)
      } else {
        toast.error(response?.message || 'Failed to load specializations')
      }
    } catch (error) {
      toast.error('Failed to load specializations. Please try again.')
    }
  }, [])

  // Load specializations on component mount
  useEffect(() => {
    handleGetSpecialization()
  }, [handleGetSpecialization])

  // Update doctor specializations when specializations are loaded or initial data changes
  const fetchData = useCallback(async () => {
    if (
      initialData?.doctorMappings?.length > 0 &&
      specializationOptions?.length > 0
    ) {
      setSpecializationDoctors((prevDoctors) =>
        prevDoctors.map((doc) => {
          if (doc?.specialization) return doc // Skip if already has a code

          // Find the specialization by display name
          const spec = specializationOptions?.find(
            (s) => s?.display === doc?.specializationDisplay
          )

          return {
            ...doc,
            specialization: spec?.code || '',
          }
        })
      )
    }
  }, [initialData?.doctorMappings, specializationOptions])

  // Call fetchData when either initialData or specializationOptions change
  useEffect(() => {
    fetchData()
  }, [fetchData])
  // State for specialization-doctor pairs
  const [specializationDoctors, setSpecializationDoctors] = useState<
    Array<{
      id: string
      specialization: string
      specializationDisplay: string
      doctor: string
      doctorsList?: Array<{
        practitionerId: string
        displayName: string
        practitionerRoleId: string
        speciality: string
      }>
      openToMaleAppointment: boolean
      openToFemaleAppointment: boolean
      active?: boolean
      isNew?: boolean // Flag to identify newly added items in edit mode
    }>
  >([
    {
      id: '1',
      specialization: '',
      specializationDisplay: '',
      doctor: '',
      openToMaleAppointment: false,
      openToFemaleAppointment: false,
      active: true,
    },
  ])

  // Fetch and process lab packages
  const fetchLabPackages = useCallback(async () => {
    setIsLoadingPackages(true)
    try {
      const packages = await getLabPackages()
      setLabPackages(packages)
    } catch (error: any) {
      toast.error(error.message || 'An error occurred while loading packages')
      setLabPackages([])
    } finally {
      setIsLoadingPackages(false)
    }
  }, [])

  useEffect(() => {
    fetchLabPackages()
  }, [fetchLabPackages])

  const handleToggleDoctorActive = useCallback(
    (id: string, e: React.ChangeEvent<HTMLInputElement>) => {
      e?.stopPropagation()
      setSpecializationDoctors((prev) =>
        prev?.map((item) =>
          item?.id === id ? { ...item, active: !item?.active } : item
        )
      )
    },
    []
  )

  const canAddNewSpecialization = useCallback(() => {
    if (!specializationDoctors?.length) return true

    if (isEdit) return true

    const lastDoctor =
      specializationDoctors?.[specializationDoctors?.length - 1]
    return (
      lastDoctor?.specialization &&
      lastDoctor?.doctor &&
      (lastDoctor?.openToMaleAppointment || lastDoctor?.openToFemaleAppointment)
    )
  }, [specializationDoctors, isEdit])

  const handleAddSpecializationDoctor = useCallback(() => {
    if (!canAddNewSpecialization()) {
      const lastDoctor =
        specializationDoctors?.[specializationDoctors?.length - 1]

      if (!lastDoctor?.specialization || !lastDoctor?.doctor) {
        toast.error(
          'Please complete the current specialization before adding a new one'
        )
      } else if (
        !lastDoctor?.openToMaleAppointment &&
        !lastDoctor?.openToFemaleAppointment
      ) {
        toast.error(
          'Please select at least one gender (Male/Female) before adding a new specialization'
        )
      } else {
        toast.error(
          'Please complete the current specialization before adding a new one'
        )
      }
      return
    }

    setSpecializationDoctors((prev) => [
      ...(prev || []),
      {
        id: `doc-${Date.now()}`,
        specialization: '',
        specializationDisplay: '',
        doctor: '',
        doctorsList: undefined,
        openToMaleAppointment: true,
        openToFemaleAppointment: true,
        active: true,
        isNew: true, // Mark as new to make it editable in edit mode
      },
    ])
  }, [canAddNewSpecialization, specializationDoctors])
  const handleSpecializationChange = useCallback(
    async (id: string, specializationCode: string) => {
      // Find the selected specialization details
      const selectedSpecialization = specializationOptions?.find(
        (option: SpecializationType) => option?.code === specializationCode
      )

      // Update the specialization with both code and display name
      setSpecializationDoctors(
        (
          prev: Array<{
            id: string
            specialization: string
            specializationDisplay: string
            doctor: string
            doctorsList?: Array<{
              practitionerId: string
              displayName: string
              practitionerRoleId: string
              speciality: string
            }>
            openToMaleAppointment: boolean
            openToFemaleAppointment: boolean
            active?: boolean
            isNew?: boolean
          }>
        ) =>
          prev?.map((item) =>
            item?.id === id
              ? {
                  ...item,
                  specialization: specializationCode,
                  specializationDisplay: selectedSpecialization?.display || '',
                  doctor: '',
                  openToMaleAppointment: true,
                  openToFemaleAppointment: true,
                  doctorsList: [],
                }
              : item
          )
      )

      if (specializationCode) {
        try {
          const response = await getDoctors(
            selectedSpecialization?.display || ''
          )
          if (response?.success) {
            // Get all doctors for the selected specialization
            const filteredDoctors = response?.data || []

            setSpecializationDoctors(
              (
                prev: Array<{
                  id: string
                  specialization: string
                  specializationDisplay: string
                  doctor: string
                  doctorsList?: Array<{
                    practitionerId: string
                    displayName: string
                    practitionerRoleId: string
                    speciality: string
                  }>
                  openToMaleAppointment: boolean
                  openToFemaleAppointment: boolean
                  active?: boolean
                  isNew?: boolean
                }>
              ) =>
                prev?.map((item) =>
                  item?.id === id
                    ? {
                        ...item,
                        doctorsList: filteredDoctors,
                      }
                    : item
                )
            )
          } else {
            toast.error(response?.message || 'Failed to load doctors')
          }
        } catch (error) {
          toast.error(`Error fetching doctors: ${error}`)
        }
      }
    },
    [specializationOptions] // Only include values that can trigger re-renders
  )

  const handleDoctorChange = useCallback(
    (id: string, practitionerId: string) => {
      setSpecializationDoctors((prev) =>
        prev?.map((item) => {
          if (item?.id === id) {
            const selectedDoctor = item?.doctorsList?.find(
              (doc) => doc?.practitionerId === practitionerId
            )
            return {
              ...item,
              doctor: practitionerId,
              displayName: selectedDoctor?.displayName || '',
            }
          }
          return item
        })
      )
    },
    []
  )
  const handleCampNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setCampName(e?.target?.value),
    []
  )

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
      setDescription(e?.target?.value),
    []
  )

  const handleLocationChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      const val = e?.target?.value
      setLocationId(val)
      const locObj = activeLocations?.find(
        (loc: any) => loc?.resource?.id === val
      )
      setLocation(locObj?.resource?.name || '')
    },
    [activeLocations]
  )

  const handleDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setDate(e?.target?.value),
    []
  )
  const handleToDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setToDate(e?.target?.value),
    []
  )

  const handleCampTypeChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      const val = e?.target?.value
      const found = options?.find(
        (o) => o?.campTypeId === val || o?.name === val
      )
      if (found) setCampType(found)
      else if (val) setCampType({ campTypeId: val, name: val, description: '' })
      else setCampType(undefined)
    },
    [options]
  )

  // const formatDateForPayload = useCallback((dateString: string) => {
  //   if (!dateString) return ''
  //   return dateString
  // }, [])

  const formatIsoToDateInput = useCallback((isoString?: string) => {
    if (!isoString) return ''
    const d = new Date(isoString)
    if (Number?.isNaN(d?.getTime())) return ''
    return d?.toISOString()?.slice(0, 10)
  }, [])

  const handleGetCampTypes = useCallback(async () => {
    const resp = await getCampTypes()
    if (resp?.success) {
      const types: CampType[] = resp?.data ?? []

      let matched: CampType | undefined = undefined
      if (initialData) {
        matched =
          types?.find((t) => t?.campTypeId === initialData?.campTypeId) ||
          types?.find((t) => t?.name === initialData?.campType)
      }

      if (matched) {
        setOptions(types)
        setCampType(matched)
      } else if (
        initialData &&
        (initialData?.campTypeId || initialData?.campType)
      ) {
        const placeholder: CampType = {
          campTypeId:
            initialData?.campTypeId ??
            `placeholder-${initialData?.campType ?? Date?.now()}`,
          name: initialData?.campType ?? '',
          description: '',
        }
        setOptions([placeholder, ...types])
        setCampType(placeholder)
      } else {
        setOptions(types)
      }
    } else {
      // optionally show error (kept silent to avoid noisy behavior)
    }
  }, [initialData])

  const [labPackageErrors, setLabPackageErrors] = useState<{
    male: string
    female: string
  }>({ male: '', female: '' })

  const validateForm = useCallback((): {
    isValid: boolean
    errors: Record<string, string>
  } => {
    const errors: Record<string, string> = {}

    // Reset lab package errors
    setLabPackageErrors({ male: '', female: '' })

    // Basic field validations
    if (!campName?.trim()) errors.campName = errorMessages?.camp?.nameRequired
    if (!locationId) errors.locationId = errorMessages?.camp?.locationSelect
    if (!location) errors.location = errorMessages?.camp?.locationRequired
    if (!date) errors.date = errorMessages?.camp?.startDateRequired
    if (!toDate) errors.toDate = errorMessages?.camp?.endDateRequired
    if (!campType?.campTypeId)
      errors.campType = errorMessages?.camp?.campTypeRequired

    // Lab package validations
    if (!selectedMalePackage?.id) {
      setLabPackageErrors((prev) => ({
        ...prev,
        male: errorMessages?.camp?.maleLabPackageRequired,
      }))
      errors.maleLabPackage = errorMessages?.camp?.maleLabPackageRequired
    }

    if (!selectedFemalePackage?.id) {
      setLabPackageErrors((prev) => ({
        ...prev,
        female: errorMessages?.camp?.femaleLabPackageRequired,
      }))
      errors.femaleLabPackage = errorMessages?.camp?.femaleLabPackageRequired
    }

    // Date validation
    if (date && toDate && new Date(date) > new Date(toDate)) {
      errors.dateRange = errorMessages?.camp?.dateRangeInvalid
    }

    // Doctor mappings validation
    if (!specializationDoctors?.length) {
      errors.doctorMappings =
        errorMessages?.camp?.doctorMappings?.atLeastOneRequired
    } else {
      // Check if at least one doctor is active
      if (
        isEdit &&
        !specializationDoctors?.some((doc) => doc?.active !== false)
      ) {
        errors.doctorMappings =
          errorMessages?.camp?.doctorMappings?.oneActiveRequired
      }

      specializationDoctors?.forEach((doc, index) => {
        if (!doc?.specialization) {
          errors[`specialization-${index}`] =
            errorMessages?.camp?.doctorMappings?.specializationRequired
        }
        if (!doc?.doctor) {
          errors[`doctor-${index}`] =
            errorMessages?.camp?.doctorMappings?.doctorRequired
        }
        if (!doc?.openToMaleAppointment && !doc?.openToFemaleAppointment) {
          errors[`appointment-${index}`] =
            errorMessages?.camp?.doctorMappings?.appointmentTypeRequired
        }
      })
    }

    return {
      isValid: Object?.keys(errors)?.length === 0,
      errors,
    }
  }, [
    campName,
    locationId,
    location,
    date,
    toDate,
    campType,
    specializationDoctors,
    selectedMalePackage?.id,
    selectedFemalePackage?.id,
    setLabPackageErrors,
    isEdit,
  ])

  const handleCreateOrUpdateCamp = useCallback(async () => {
    try {
      // Validate form before submission
      const { isValid, errors } = validateForm()
      if (!isValid) {
        // Show all errors for debugging
        Object.values(errors).forEach((error) => {
          if (error) {
            toast.error(error, { toastId: `validation-error-${Date.now()}` })
          }
        })
        return
      }

      setLoading(true)

      if (isEdit && initialData && (initialData?.campId || initialData?.id)) {
        const payload: any = {
          campId: initialData?.campId ?? initialData?.id ?? '',
          name: campName?.trim(),
          description: description?.trim(),
          location: location?.trim(),
          locationId: locationId,
          fromDate: date,
          toDate: toDate,
          campTypeId: campType?.campTypeId,
          campType: campType?.name,
          campSponsor: campSponsor.trim(),
          expectedFootFall: expectedFootFall,
          // postalCode: postalCode,
          numberOfRegistrations: initialData?.numberOfRegistrations ?? null,
          numberOfVisits: initialData?.numberOfVisits ?? null,
          compliance: initialData?.compliance ?? null,
          // followUpActions: initialData?.followUpActions ?? null,
          doctorMappings: specializationDoctors?.map((doc) => {
            const selectedDoctor =
              doc?.doctorsList?.find(
                (d) => d?.practitionerId === doc?.doctor
              ) || doc?.doctorsList?.[0]
            return {
              doctor: {
                practitionerId:
                  doc?.doctor || selectedDoctor?.practitionerId || '',
                practitionerRoleId: selectedDoctor?.practitionerRoleId || '',
                displayName: selectedDoctor?.displayName || doc?.doctor || '',
                speciality: doc?.specializationDisplay || '',
              },
              active: doc?.active,
              openToMaleAppointment: doc?.openToMaleAppointment,
              openToFemaleAppointment: doc?.openToFemaleAppointment,
            }
          }),
          maleLabOrderPackageId: selectedMalePackage?.id || '',
          maleLabOrderPackageName: selectedMalePackage?.name || '',
          femaleLabOrderPackageId: selectedFemalePackage?.id || '',
          femaleLabOrderPackageName: selectedFemalePackage?.name || '',
          campCategory: campFeeType,
          ecgNormalSend: ecgNormalSend,
          ecgAbnormalSend: ecgAbnormalSend,
          pftNormalSend: pftNormalSend,
          pftAbnormalSend: pftAbnormalSend,
        }

        const res = await updateCamp(payload)
        if (res?.success) {
          // Show success message
          toast(
            <ToastMessage
              message={res?.message || errorMessages?.camp?.success?.updated}
            />,
            {
              ...toastOptions,
              type: 'success',
            }
          )
          onSaved && onSaved(res?.data ?? res)
        } else if (res?.message) {
          // Show error message from response
          toast(<ToastMessage message={res.message} />, {
            ...toastOptions,
            type: 'error',
          })
        } else {
          // Show generic error message
          toast(
            <ToastMessage
              message={errorMessages?.camp?.success?.updateFailed}
            />,
            {
              ...toastOptions,
              type: 'error',
            }
          )
        }

        // Reset form and close in all cases
        handleClose()
        setCampName('')
        setDescription('')
        setLocationId('')
        setLocation('')
        setDate('')
        setToDate('')
        setCampType(undefined)
        setEcgNormalSend(false)
        setEcgAbnormalSend(false)
        setPftNormalSend(false)
        setPftAbnormalSend(false)
        initializedRef.current = false
      } else {
        // Handle create new camp
        const payload: any = {
          name: campName.trim(),
          description: description.trim(),
          location: location.trim(),
          locationId: locationId,
          fromDate: date,
          toDate: toDate,
          campTypeId: campType?.campTypeId,
          campType: campType?.name,
          campSponsor: campSponsor.trim(),
          expectedFootFall: expectedFootFall,
          doctorMappings: specializationDoctors?.map((doc) => {
            const selectedDoctor =
              doc?.doctorsList?.find(
                (d) => d?.practitionerId === doc?.doctor
              ) || doc?.doctorsList?.[0]
            return {
              doctor: {
                practitionerId:
                  doc?.doctor || selectedDoctor?.practitionerId || '',
                practitionerRoleId: selectedDoctor?.practitionerRoleId || '',
                displayName: selectedDoctor?.displayName || doc?.doctor || '',
                speciality: doc?.specializationDisplay || '',
              },
              active: doc?.active,
              openToMaleAppointment: doc?.openToMaleAppointment,
              openToFemaleAppointment: doc?.openToFemaleAppointment,
            }
          }),
          maleLabOrderPackageId: selectedMalePackage?.id || '',
          maleLabOrderPackageName: selectedMalePackage?.name || '',
          femaleLabOrderPackageId: selectedFemalePackage?.id || '',
          femaleLabOrderPackageName: selectedFemalePackage?.name || '',
          campCategory: campFeeType,
          ecgNormalSend: ecgNormalSend,
          ecgAbnormalSend: ecgAbnormalSend,
          pftNormalSend: pftNormalSend,
          pftAbnormalSend: pftAbnormalSend,
        }

        const res = await createCamp(payload)

        if (res?.success) {
          toast(
            <ToastMessage
              message={res?.message || 'Camp created successfully'}
            />,
            {
              ...toastOptions,
              type: 'success',
            }
          )
          onSaved && onSaved(res?.data ?? res)
          handleClose()
        } else if (res?.message) {
          toast(<ToastMessage message={res.message} />, {
            ...toastOptions,
            type: 'error',
          })
        } else {
          toast(<ToastMessage message='Failed to create camp' />, {
            ...toastOptions,
            type: 'error',
          })
        }
      }
    } catch (err) {
      toast(<ToastMessage message={'Failed to save camp'} />, {
        ...toastOptions,
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }, [
    validateForm,
    isEdit,
    initialData,
    campName,
    description,
    campType,
    location,
    locationId,
    date,
    toDate,
    campSponsor,
    expectedFootFall,
    onSaved,
    handleClose,
    specializationDoctors,
    selectedMalePackage?.id,
    selectedMalePackage?.name,
    selectedFemalePackage?.id,
    selectedFemalePackage?.name,
    campFeeType,
    ecgNormalSend,
    ecgAbnormalSend,
    pftNormalSend,
    pftAbnormalSend,
    // postalCode,
  ])

  const initializeForm = useCallback(() => {
    if (initialData) {
      // Set basic camp info
      setCampName(initialData?.name || '')
      setDescription(initialData?.description || '')
      setLocationId(initialData?.locationId || '')
      setLocation(initialData?.location || '')
      setCampSponsor(initialData?.campSponsor || '')
      setExpectedFootFall(initialData?.expectedFootFall || '')

      // Set dates
      if (initialData?.fromDate || initialData?.date) {
        setDate(
          formatIsoToDateInput(initialData?.fromDate || initialData?.date)
        )
      }

      if (initialData?.toDate || initialData?.endDate) {
        setToDate(
          formatIsoToDateInput(initialData?.toDate || initialData?.endDate)
        )
      }

      // Set camp type if available
      if (initialData?.campTypeId || initialData?.campType) {
        setCampType({
          campTypeId: initialData?.campTypeId || '',
          name: initialData?.campType || '',
          description: '',
        })
      } else {
        setCampType(undefined)
      }

      // Initialize doctor mappings
      if (initialData?.doctorMappings?.length > 0) {
        const formattedDoctors = initialData?.doctorMappings?.map(
          (mapping: any, index: number) => {
            // Find the specialization code that matches the display name
            const specialization = specializationOptions?.find(
              (s: SpecializationType) =>
                s.display === mapping?.doctor?.speciality
            )

            return {
              id: `doc-${index}`,
              specialization: specialization?.code || '',
              specializationDisplay: mapping?.doctor?.speciality || '',
              doctor: mapping?.doctor?.practitionerId || '',
              doctorsList: [
                {
                  practitionerId: mapping?.doctor?.practitionerId || '',
                  practitionerRoleId: mapping?.doctor?.practitionerRoleId || '',
                  displayName: mapping?.doctor?.displayName || '',
                  speciality: mapping?.doctor?.speciality || '',
                },
              ],
              active: mapping?.active ?? true,
              openToMaleAppointment: mapping?.openToMaleAppointment || false,
              openToFemaleAppointment:
                mapping?.openToFemaleAppointment || false,
            }
          }
        )
        setSpecializationDoctors(formattedDoctors)
      } else {
        setSpecializationDoctors([])
      }

      // Set male lab package if available
      if (initialData?.maleLabOrderPackageId) {
        setSelectedMalePackage({
          id: initialData?.maleLabOrderPackageId,
          name: initialData?.maleLabOrderPackageName || '',
          price: 0,
          panels: [],
          lab_tests: [],
          status: 'active',
          active_from: null,
          active_till: null,
          package_id: parseInt(initialData?.maleLabOrderPackageId, 10) || null,
          created_by: '',
          created_at: new Date().toISOString(),
          updated_by: '',
          updated_at: new Date().toISOString(),
        })
      } else {
        setSelectedMalePackage(null)
      }

      // Set female lab package if available
      if (initialData?.femaleLabOrderPackageId) {
        setSelectedFemalePackage({
          id: initialData?.femaleLabOrderPackageId,
          name: initialData?.femaleLabOrderPackageName || '',
          price: 0,
          panels: [],
          lab_tests: [],
          status: 'active',
          active_from: null,
          active_till: null,
          package_id:
            parseInt(initialData?.femaleLabOrderPackageId, 10) || null,
          created_by: '',
          created_at: new Date().toISOString(),
          updated_by: '',
          updated_at: new Date().toISOString(),
        })
      } else {
        setSelectedFemalePackage(null)
      }

      // Set camp fee type if available
      setCampFeeType(initialData?.campCategory || 'free')

      // Set ECG / PFT notification settings
      setEcgNormalSend(initialData?.ecgNormalSend ?? false)
      setEcgAbnormalSend(initialData?.ecgAbnormalSend ?? false)
      setPftNormalSend(initialData?.pftNormalSend ?? false)
      setPftAbnormalSend(initialData?.pftAbnormalSend ?? false)

      // Set location from activeLocations
      if (activeLocations) {
        const locObj = activeLocations.find(
          (loc: any) =>
            loc?.resource?.id ===
            (initialData?.locationId || initialData?.resource?.locationId)
        )
        if (locObj) {
          setLocation(locObj.resource?.name || initialData?.location || '')
        }
      }
    } else {
      // Reset form if no initial data
      setCampName('')
      setDescription('')
      setLocationId('')
      setLocation('')
      setDate('')
      setToDate('')
      setCampType(undefined)
      setSpecializationDoctors([])
      setEcgNormalSend(false)
      setEcgAbnormalSend(false)
      setPftNormalSend(false)
      setPftAbnormalSend(false)
    }
  }, [
    initialData,
    activeLocations,
    formatIsoToDateInput,
    specializationOptions,
    setCampFeeType,
  ])

  const syncCampType = useCallback(() => {
    if (!options?.length) return

    if (campType?.campTypeId) {
      const existing = options?.find(
        (t: any) => t?.campTypeId === campType?.campTypeId
      )
      if (existing && existing !== campType) setCampType(existing)
      return
    }

    if (initialData) {
      const match =
        options?.find((t: any) => t?.campTypeId === initialData?.campTypeId) ||
        options?.find((t: any) => t?.name === initialData?.campType)
      if (match && match !== campType) setCampType(match)
    }
  }, [options, campType, initialData])

  const initializeDialog = useCallback(async () => {
    if (initializedRef?.current) return

    initializedRef.current = true
    await handleGetCampTypes()
    initializeForm()
  }, [handleGetCampTypes, initializeForm])

  // const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const value = e?.target?.value
  //   if (value === '' || (value.length <= 6 && /^\d*$/.test(value))) {
  //     setPostalCode(value)
  //   }
  // }

  useEffect(() => {
    if (!open) {
      initializedRef.current = false
      return
    }

    void initializeDialog()
  }, [open, initializeDialog])

  useEffect(() => {
    syncCampType()
  }, [syncCampType])

  const renderLocationOptions = useCallback(() => {
    return activeLocations?.map((loc: any) => (
      <MenuItem key={loc?.resource?.id} value={loc?.resource?.id}>
        {loc?.resource?.name}
      </MenuItem>
    ))
  }, [activeLocations])

  // Male Lab Package Select Component
  const renderMaleLabPackageSelect = () => (
    <FormControl
      variant='outlined'
      className={styles.formInputContainer}
      fullWidth
      error={!!labPackageErrors?.male}
    >
      <Select
        id='male-lab-package'
        value={selectedMalePackage?.id || ''}
        onChange={(e) => {
          const selected = labPackages.find((pkg) => pkg.id === e.target.value)
          setSelectedMalePackage(selected || null)
        }}
        displayEmpty
        inputProps={{ 'aria-label': 'Male Lab Package' }}
        className={styles.formSelect}
        IconComponent={ArrowDropDownIcon}
      >
        <MenuItem value=''>Select male lab package</MenuItem>
        {isLoadingPackages ? (
          <MenuItem disabled>Loading packages...</MenuItem>
        ) : (
          labPackages
            ?.filter((pkg: any) => {
              const isActive = pkg?.status?.toLowerCase() === activeText
              const activeTill = pkg?.active_till
                ? new Date(pkg?.active_till)
                : null
              const isDateValid = !activeTill || activeTill >= new Date()
              return isActive && isDateValid
            })
            ?.sort((a: any, b: any) => a?.name?.localeCompare(b?.name))
            ?.map((pkg: any) => (
              <MenuItem key={pkg?.id} value={pkg?.id}>
                {pkg?.name}
              </MenuItem>
            ))
        )}
      </Select>
    </FormControl>
  )

  // Female Lab Package Select Component
  const renderFemaleLabPackageSelect = () => (
    <FormControl
      variant='outlined'
      className={styles.formInputContainer}
      fullWidth
      error={!!labPackageErrors?.female}
    >
      <Select
        id='female-lab-package'
        value={selectedFemalePackage?.id || ''}
        onChange={(e) => {
          const selected = labPackages.find((pkg) => pkg.id === e.target.value)
          setSelectedFemalePackage(selected || null)
        }}
        displayEmpty
        inputProps={{ 'aria-label': 'Female Lab Package' }}
        className={styles.formSelect}
        IconComponent={ArrowDropDownIcon}
      >
        <MenuItem value=''>Select female lab package</MenuItem>
        {isLoadingPackages ? (
          <MenuItem disabled>Loading packages...</MenuItem>
        ) : (
          labPackages
            ?.filter((pkg: any) => {
              const isActive = pkg?.status?.toLowerCase() === activeText
              const activeTill = pkg?.active_till
                ? new Date(pkg?.active_till)
                : null
              const isDateValid = !activeTill || activeTill >= new Date()
              return isActive && isDateValid
            })
            ?.sort((a: any, b: any) => a?.name?.localeCompare(b?.name))
            ?.map((pkg: any) => (
              <MenuItem key={pkg?.id} value={pkg?.id}>
                {pkg?.name}
              </MenuItem>
            ))
        )}
      </Select>
    </FormControl>
  )

  return (
    <Box className={`${styles.createCampContainer} ${className}`}>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth='xs'
        className={styles.createCampDialog}
        PaperProps={{ className: styles.createCampWrapper }}
      >
        <DialogTitle>
          <Typography variant='h6' align='center'>
            {isEdit ? 'Edit Camp' : 'Create Camp'}
          </Typography>
        </DialogTitle>

        <DialogContent>
          {loading ? (
            <Box className={styles.loaderContainer}>
              <ScaleLoader color='#004C91' />
            </Box>
          ) : (
            <Box className={styles.createCampForm}>
              {/* Camp Name */}
              <Box className={styles.formRow}>
                <label className={styles.formLabel} htmlFor='camp-name'>
                  Camp Name<span className={styles.requiredField}></span>
                </label>
                <input
                  id='camp-name'
                  className={styles.formInput}
                  value={campName}
                  onChange={handleCampNameChange}
                  placeholder='Enter camp name'
                />
              </Box>

              {/* Description */}
              <Box className={styles.formRow}>
                <label className={styles.formLabel} htmlFor='camp-description'>
                  Description
                </label>
                <textarea
                  id='camp-description'
                  className={`${styles.formInput} ${styles.formTextarea}`}
                  value={description}
                  onChange={handleDescriptionChange}
                  rows={2}
                  placeholder='Description'
                />
              </Box>

              {/* Location */}
              <Box className={styles.formRow}>
                <label className={styles.formLabel} htmlFor='camp-location'>
                  Location<span className={styles.requiredField}></span>
                </label>
                <FormControl
                  variant='outlined'
                  className={styles.formInputContainer}
                >
                  <Select
                    id='camp-location'
                    value={locationId}
                    onChange={handleLocationChange}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Location' }}
                    className={styles.formSelect}
                    IconComponent={ArrowDropDownIcon}
                  >
                    <MenuItem value=''>Select location</MenuItem>
                    {renderLocationOptions()}
                  </Select>
                </FormControl>
              </Box>

              {/* Lab Packages */}
              <Box className={styles.formRow}>
                <label className={styles.formLabel} htmlFor='male-lab-package'>
                  Male Lab Package{' '}
                  <span className={styles.requiredAsterisk}>*</span>
                </label>
                {renderMaleLabPackageSelect()}
                {labPackageErrors?.male && (
                  <Typography color='error' variant='caption' display='block'>
                    {labPackageErrors?.male}
                  </Typography>
                )}
              </Box>

              <Box className={styles.formRow}>
                <label
                  className={styles.formLabel}
                  htmlFor='female-lab-package'
                >
                  Female Lab Package{' '}
                  <span className={styles.requiredAsterisk}>*</span>
                </label>
                {renderFemaleLabPackageSelect()}
                {labPackageErrors?.female && (
                  <Typography color='error' variant='caption' display='block'>
                    {labPackageErrors?.female}
                  </Typography>
                )}
              </Box>

              {/* From Date */}
              <Box className={styles.formRow}>
                <label className={styles.formLabel} htmlFor='camp-from-date'>
                  From Date<span className={styles.requiredField}></span>
                </label>
                <input
                  id='camp-from-date'
                  aria-label='From Date'
                  className={styles.formInput}
                  type='date'
                  value={date}
                  onChange={handleDateChange}
                />
              </Box>

              {/* To Date */}
              <Box className={styles.formRow}>
                <label className={styles.formLabel} htmlFor='camp-to-date'>
                  To Date<span className={styles.requiredField}></span>
                </label>
                <input
                  id='camp-to-date'
                  aria-label='To Date'
                  className={styles.formInput}
                  type='date'
                  value={toDate}
                  onChange={handleToDateChange}
                  min={date} // Ensure to date is not before from date
                />
              </Box>

              {/* Camp Sponsor */}
              <Box className={styles.formRow}>
                <label className={styles.formLabel} htmlFor='camp-sponsor'>
                  Camp Sponsor
                </label>
                <input
                  id='camp-sponsor'
                  className={styles.formInput}
                  value={campSponsor}
                  onChange={(e) => setCampSponsor(e.target.value)}
                  placeholder='Enter camp sponsor name'
                />
              </Box>

              {/* Expected Foot Fall */}
              <Box className={styles.formRow}>
                <label
                  className={styles.formLabel}
                  htmlFor='expected-foot-fall'
                >
                  Expected Foot Fall
                  <span className={styles.requiredField}></span>
                </label>
                <input
                  id='expected-foot-fall'
                  type='number'
                  min='0'
                  className={styles.formInput}
                  value={expectedFootFall}
                  onChange={(e) => setExpectedFootFall(e?.target?.value)}
                  placeholder='Enter expected number of attendees'
                />
              </Box>

              {/* Camp Type */}
              <Box className={styles.formRow}>
                <label className={styles.formLabel} htmlFor='camp-type'>
                  Camp Type<span className={styles.requiredField}></span>
                </label>
                <FormControl
                  variant='outlined'
                  className={styles.formInputContainer}
                >
                  <Select
                    id='camp-type'
                    value={campType?.campTypeId ?? campType?.name ?? ''}
                    onChange={handleCampTypeChange}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Type' }}
                    className={styles.formSelect}
                    IconComponent={ArrowDropDownIcon}
                  >
                    <MenuItem value=''>Select type</MenuItem>
                    {options?.map((opt) => (
                      <MenuItem
                        key={opt?.campTypeId || opt?.name}
                        value={opt?.campTypeId || opt?.name}
                      >
                        {opt?.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* ECG Notification */}
              <Box className={styles.formRow}>
                <Box display='flex' alignItems='center' gap={0.5}>
                  <Typography className={styles.formLabel}>ECG</Typography>
                  <Tooltip
                    title='Select Normal or Abnormal to automatically send a WhatsApp message to the patient when an ECG interpretation is given.'
                    arrow
                    placement='top'
                  >
                    <IconButton
                      size='small'
                      tabIndex={-1}
                      className={styles.infoIconButton}
                    >
                      <InfoOutlinedIcon className={styles.infoIcon} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box display='flex' alignItems='center' gap={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size='small'
                        checked={ecgAbnormalSend}
                        onChange={(e) => setEcgAbnormalSend(e.target.checked)}
                      />
                    }
                    label='Abnormal'
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size='small'
                        checked={ecgNormalSend}
                        onChange={(e) => setEcgNormalSend(e.target.checked)}
                      />
                    }
                    label='Normal'
                  />
                </Box>
              </Box>

              {/* PFT Notification */}
              <Box className={styles.formRow}>
                <Box display='flex' alignItems='center' gap={0.5}>
                  <Typography className={styles.formLabel}>PFT</Typography>
                  <Tooltip
                    title='Select Normal or Abnormal to automatically send a WhatsApp message to the patient when a PFT interpretation is given.'
                    arrow
                    placement='top'
                  >
                    <IconButton
                      size='small'
                      tabIndex={-1}
                      className={styles.infoIconButton}
                    >
                      <InfoOutlinedIcon className={styles.infoIcon} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box display='flex' alignItems='center' gap={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size='small'
                        checked={pftAbnormalSend}
                        onChange={(e) => setPftAbnormalSend(e.target.checked)}
                      />
                    }
                    label='Abnormal'
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size='small'
                        checked={pftNormalSend}
                        onChange={(e) => setPftNormalSend(e.target.checked)}
                      />
                    }
                    label='Normal'
                  />
                </Box>
              </Box>

              {/* Postal Code */}
              {/* <Box className={styles.formRow}>
                <label className={styles.formLabel} htmlFor='postal-code'>
                  Postal Code
                </label>
                <input
                  id='postal-code'
                  className={styles.formInput}
                  type='number'
                  min='100000'
                  max='999999'
                  maxLength={6}
                  value={postalCode}
                  onChange={handlePostalCodeChange}
                  placeholder='Enter 6-digit postal code'
                />
              </Box> */}

              {/* Camp Category */}
              <Box className={styles.formRow}>
                <label className={styles.formLabel}>Camp Category</label>
                <FormControl
                  component='fieldset'
                  className={styles.formInputContainer}
                >
                  <RadioGroup
                    row
                    aria-label='camp-fee-type'
                    name='camp-fee-type'
                    value={campFeeType}
                    className={styles.radioGroupSpacing}
                    onChange={(e) =>
                      setCampFeeType(e?.target?.value as 'paid' | 'free')
                    }
                  >
                    <FormControlLabel
                      value='paid'
                      control={<Radio size='small' />}
                      label='Paid'
                    />
                    <FormControlLabel
                      value='free'
                      control={<Radio size='small' />}
                      label='Free'
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              <Box className={`${styles.formRow} ${styles.formRowWithMargin}`}>
                <Box
                  display='flex'
                  justifyContent='space-between'
                  alignItems='center'
                  mb={1}
                >
                  <Typography variant='subtitle2' className={styles.formLabel}>
                    Specialization & Doctors
                  </Typography>
                  {/* <Button
                    variant='outlined'
                    size='small'
                    startIcon={<AddIcon />}
                    onClick={handleAddSpecializationDoctor}
                    className={styles.buttonNoUppercase}
                    disabled={
                      specializationDoctors.length > 0 &&
                      !canAddNewSpecialization()
                    }
                    title={
                      specializationDoctors.length > 0 &&
                      !canAddNewSpecialization()
                        ? 'Please complete the current specialization first'
                        : 'Add new specialization'
                    }
                  >
                    Add
                  </Button> */}
                </Box>

                {specializationDoctors?.map((item) => (
                  <Grid
                    container
                    spacing={1}
                    key={item?.id}
                    alignItems='center'
                    className={styles.specializationRow}
                  >
                    <Grid item xs={5}>
                      <FormControl fullWidth size='small' variant='outlined'>
                        <Select
                          value={item?.specialization}
                          onChange={(e) => {
                            if (!isEdit || item?.isNew) {
                              handleSpecializationChange(
                                item?.id,
                                e?.target?.value
                              )
                            }
                          }}
                          displayEmpty
                          className={styles.formSelect}
                          size='small'
                          disabled={isEdit && !item?.isNew}
                        >
                          <MenuItem value=''>
                            <em>Select Specialization</em>
                          </MenuItem>
                          {specializationOptions?.map((spec) => (
                            <MenuItem key={spec?.code} value={spec?.code}>
                              {spec?.display}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={5}>
                      <FormControl fullWidth size='small' variant='outlined'>
                        <Select
                          value={item?.doctor || ''}
                          onChange={(e) => {
                            if (!isEdit || item?.isNew) {
                              handleDoctorChange(item?.id, e?.target?.value)
                            }
                          }}
                          displayEmpty
                          className={styles.formSelect}
                          size='small'
                          disabled={
                            !item?.specialization ||
                            !item?.doctorsList?.length ||
                            (isEdit && !item?.isNew)
                          }
                          renderValue={(selected) => {
                            if (!selected) {
                              return <em>Select Doctor</em>
                            }
                            const selectedDoctor = item?.doctorsList?.find(
                              (d) => d.practitionerId === selected
                            )
                            return (
                              selectedDoctor?.displayName ||
                              selectedDoctor?.practitionerId
                            )
                          }}
                        >
                          <MenuItem value=''>
                            <em>Select Doctor</em>
                          </MenuItem>
                          {item?.doctorsList?.length ? (
                            item?.doctorsList.map((doctor) => (
                              <MenuItem
                                key={doctor?.practitionerId}
                                value={doctor?.practitionerId}
                              >
                                {doctor?.displayName ||
                                  `Doctor ${doctor?.practitionerId}`}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled>No doctors available</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={2}>
                      <Box
                        display='flex'
                        alignItems='center'
                        justifyContent='center'
                        gap={1}
                      >
                        {!item?.isNew ? (
                          <StyledToggleButton
                            value='active'
                            selected={!!item?.active}
                            onClick={(e) => {
                              e.stopPropagation()
                              const currentActive =
                                item?.active === undefined ? true : item?.active
                              const newValue = !currentActive
                              if (isEdit) {
                                handleToggleDoctorActive(item?.id, e as any)
                              } else {
                                setSpecializationDoctors((prev) =>
                                  prev?.map((doc) =>
                                    doc?.id === item?.id
                                      ? { ...doc, active: newValue }
                                      : doc
                                  )
                                )
                              }
                            }}
                            aria-label={
                              item?.active === false ? 'Inactive' : 'Active'
                            }
                          />
                        ) : (
                          <IconButton
                            size='small'
                            onClick={(e) => {
                              e?.stopPropagation()
                              setSpecializationDoctors((prev) =>
                                prev?.filter((doc) => doc?.id !== item?.id)
                              )
                            }}
                            color='error'
                            aria-label='delete'
                          >
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        )}
                      </Box>
                    </Grid>
                    {item?.doctor && (
                      <Grid
                        item
                        xs={12}
                        container
                        spacing={1}
                        className={styles.formGridContainer}
                      >
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={item?.openToMaleAppointment}
                                onChange={(e) => {
                                  // Only allow changes if not in edit mode or if it's a new item
                                  if (!isEdit || item?.isNew) {
                                    setSpecializationDoctors((prev) =>
                                      prev?.map((doc) =>
                                        doc?.id === item?.id
                                          ? {
                                              ...doc,
                                              openToMaleAppointment:
                                                e?.target?.checked,
                                            }
                                          : doc
                                      )
                                    )
                                  }
                                }}
                                color='primary'
                                disabled={isEdit && !item?.isNew}
                              />
                            }
                            label='Male'
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={item?.openToFemaleAppointment}
                                onChange={(e) => {
                                  if (!isEdit || item?.isNew) {
                                    setSpecializationDoctors((prev) =>
                                      prev?.map((doc) =>
                                        doc?.id === item?.id
                                          ? {
                                              ...doc,
                                              openToFemaleAppointment:
                                                e?.target?.checked,
                                            }
                                          : doc
                                      )
                                    )
                                  }
                                }}
                                color='primary'
                                disabled={isEdit && !item?.isNew}
                              />
                            }
                            label='Female '
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                ))}

                {/* Add button below the list */}
                <Box mt={2} display='flex' justifyContent='flex-end'>
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={<AddIcon />}
                    onClick={handleAddSpecializationDoctor}
                    className={styles.buttonNoUppercase}
                    disabled={
                      specializationDoctors?.length > 0 &&
                      !canAddNewSpecialization()
                    }
                    title={
                      specializationDoctors?.length > 0 &&
                      !canAddNewSpecialization()
                        ? 'Please complete the current specialization first'
                        : 'Add new specialization'
                    }
                  >
                    Add Specialization
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        {!loading && (
          <DialogActions className={styles.formActions}>
            <Button onClick={handleClose} className={styles.cancelButton}>
              Cancel
            </Button>
            <Button
              variant='contained'
              className={styles.submitButton}
              onClick={() => void handleCreateOrUpdateCamp()}
            >
              {isEdit ? 'Update' : 'Save'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  )
}

export default CreateCamp
